import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { ArrowLeft, CheckCircle, Loader2 } from 'lucide-react';
import Breadcrumb from '../components/Breadcrumb';
import ListingForm from '../components/ListingForm';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EditListingPage = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const { user, token, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [listingData, setListingData] = useState(null);

  useEffect(() => {
    if (authLoading) return;
    if (id && token) {
      fetchListing();
    } else if (!authLoading && !token) {
      navigate('/');
    }
  }, [id, token, authLoading]);

  const fetchListing = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/listings/${id}`);
      const listing = response.data;
      
      // Check if user can edit this listing
      if (user.role !== 'admin' && listing.user_id !== user.id) {
        navigate('/');
        return;
      }

      setListingData(listing);
    } catch (err) {
      console.error('Error fetching listing:', err);
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  if (!user && !authLoading) {
    return (
      <div className="min-h-screen bg-zinc-800 flex items-center justify-center p-4">
        <div className="bg-zinc-900 rounded-2xl p-8 border border-zinc-800 text-center max-w-md">
          <h2 className="text-2xl font-bold text-white mb-4">Login Required</h2>
          <p className="text-zinc-400 mb-6">Please login to edit listings.</p>
          <Button onClick={() => navigate('/')} className="bg-emerald-600 hover:bg-emerald-700">
            Back to Home
          </Button>
        </div>
      </div>
    );
  }

  if (loading || authLoading) {
    return (
      <div className="min-h-screen bg-zinc-800 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-emerald-500 animate-spin" />
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-zinc-800 flex items-center justify-center p-4">
        <div className="bg-zinc-900 rounded-2xl p-8 border border-zinc-800 text-center max-w-md">
          <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-8 h-8 text-emerald-500" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-4">{t('listing.listingUpdated', 'Listing Updated!')}</h2>
          <p className="text-zinc-400 mb-6">{t('admin.changesSaved', 'Your changes have been saved successfully.')}</p>
          <div className="space-y-3">
            <Button
              onClick={() => navigate(user.role === 'admin' ? '/admin' : '/my-listings')}
              className="w-full bg-emerald-600 hover:bg-emerald-700"
            >
              {user.role === 'admin' ? t('admin.backToPanel', 'Back to Admin Panel') : t('profile.backToMyListings', 'Back to My Listings')}
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate(`/escort/${id}`)}
              className="w-full border-zinc-700 text-zinc-300 hover:bg-zinc-800"
            >
              {t('listing.viewListing', 'View Listing')}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const handleSubmit = async (formData) => {
    setSubmitting(true);
    setError('');
    
    try {
      await axios.put(`${API}/listings/${id}`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubmitted(true);
    } catch (err) {
      console.error('Error updating listing:', err);
      setError(err.response?.data?.detail || t('admin.listingUpdateError', 'Failed to update listing. Please try again.'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-800">
      {/* Breadcrumb */}
      <Breadcrumb items={[
        { label: t('listing.myListings', 'My Listings'), href: '/my-listings' },
        { label: t('listing.editListing', 'Edit Listing'), href: null }
      ]} />

      {/* Hero Section */}
      <div className="border-b border-zinc-800 py-12 bg-gradient-to-b from-zinc-900 to-black">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">{t('listing.editListing', 'Edit Listing')}</h1>
          <p className="text-zinc-400 text-lg max-w-2xl mx-auto">
            {t('admin.updateListingInfo', 'Update your listing information')}
          </p>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">

        <div className="max-w-2xl mx-auto">
          <div className="bg-zinc-900 rounded-2xl p-6 md:p-8 border border-zinc-800">

            <ListingForm
              initialData={listingData}
              onSubmit={handleSubmit}
              submitting={submitting}
              error={error}
              submitButtonText={t('common.save', 'Save Changes')}
              loadingButtonText={t('common.saving', 'Saving...')}
              token={token}
              isAdmin={user?.role === 'admin'}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditListingPage;
